import { cn } from "@bcc/lib/utils";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setOnboardingStepperTestId } from "./onboarding-stepper.utils";

interface Step {
  number: number;
  label: string;
  status: "complete" | "current" | "upcoming";
}

interface OnboardingStepperProps {
  currentStep: number;
  step2Label?: string;
  testId?: string;
}

export function OnboardingStepper({ currentStep, step2Label, testId }: OnboardingStepperProps) {
  const { t } = useTranslation();
  const steps: Step[] = [
    {
      number: 1,
      label: t("onboarding.stepper.connect"),
      status: currentStep > 1 ? "complete" : currentStep === 1 ? "current" : "upcoming",
    },
    {
      number: 2,
      label: step2Label ?? t("onboarding.stepper.mapProducts"),
      status: currentStep > 2 ? "complete" : currentStep === 2 ? "current" : "upcoming",
    },
    {
      number: 3,
      label: t("onboarding.stepper.done"),
      status: currentStep >= 3 ? "complete" : "upcoming",
    },
  ];

  return (
    <div data-testid={testId ?? setOnboardingStepperTestId("container")} className="flex items-center justify-between">
      {steps.map((step, index) => (
        <div key={step.number} className="flex flex-1 items-center">
          <div className="flex flex-col items-center gap-2">
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors",
                step.status === "complete" && "border-primary bg-primary text-primary-foreground",
                step.status === "current" && "border-primary bg-white text-primary",
                step.status === "upcoming" && "border-border bg-white text-muted-foreground"
              )}
            >
              {step.status === "complete" ? <Check className="h-5 w-5" /> : step.number}
            </div>
            <span
              className={cn(
                "text-sm font-medium",
                step.status === "current" && "text-primary",
                step.status === "complete" && "text-primary",
                step.status === "upcoming" && "text-muted-foreground"
              )}
            >
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className={cn("mx-4 h-0.5 flex-1 transition-colors", step.status === "complete" ? "bg-primary" : "bg-border")} />
          )}
        </div>
      ))}
    </div>
  );
}
